import { useState } from 'react'
import type { SupportRequestType } from '@fellowship/shared'
import { useAuthStore } from '../store/auth'
import { useSupportRequest } from '../hooks/useSupportRequest'

const TYPE_OPTIONS: { value: SupportRequestType; label: string }[] = [
  { value: 'bug', label: 'Bug' },
  { value: 'feature', label: 'Idea' },
  { value: 'question', label: 'Help' },
]

const PLACEHOLDERS: Record<SupportRequestType, string> = {
  bug: "What went wrong? What were you trying to do?",
  feature: "What would you like to see?",
  question: "What can we help with?",
}

export function HelpWidget() {
  const { user } = useAuthStore()
  const mutation = useSupportRequest()

  const [open, setOpen] = useState(false)
  const [type, setType] = useState<SupportRequestType>('bug')
  const [message, setMessage] = useState('')
  const [email, setEmail] = useState('')

  function close() {
    setOpen(false)
    // Reset after the close so the panel doesn't flash empty while animating out.
    setTimeout(() => {
      mutation.reset()
      setMessage('')
      setEmail('')
      setType('bug')
    }, 150)
  }

  function submit(e: React.FormEvent) {
    e.preventDefault()
    if (!message.trim()) return
    mutation.mutate({
      type,
      message: message.trim(),
      email: user ? undefined : email.trim() || undefined,
      pageUrl: window.location.href,
    })
  }

  const submitted = mutation.isSuccess

  return (
    <>
      {/* Floating trigger — sits below the mobile drawer (z-200) so it never overlaps an open menu */}
      <button
        type="button"
        aria-label="Help & feedback"
        onClick={() => (open ? close() : setOpen(true))}
        className="fixed bottom-5 right-5 z-[190] w-12 h-12 rounded-full bg-primary text-on-primary shadow-brand-glow flex items-center justify-center text-[22px] font-semibold cursor-pointer border-0 transition-transform hover:scale-105"
      >
        {open ? '×' : '?'}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Help and feedback"
          className="fixed bottom-20 right-5 z-[190] w-[calc(100vw-2.5rem)] max-w-[360px] bg-surface rounded-2xl border border-line-soft overflow-hidden"
          style={{ boxShadow: '0 12px 48px rgba(0,0,0,0.22)' }}
        >
          {submitted ? (
            <div className="p-6 text-center">
              <div className="text-[40px] leading-none mb-3">🙏</div>
              <h2 className="m-0 mb-1.5 text-[16px] font-semibold text-ink">Thank you!</h2>
              <p className="m-0 mb-5 text-[14px] text-ink-2">
                We got your message and read every one.
              </p>
              <button
                onClick={close}
                className="px-4 py-2 rounded-lg border-0 bg-primary text-on-primary text-[13px] font-semibold cursor-pointer font-sans"
              >
                Done
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="p-5">
              <h2 className="m-0 mb-1 text-[16px] font-semibold text-ink">How can we help?</h2>
              <p className="m-0 mb-4 text-[13px] text-ink-2">
                Found a bug, have an idea, or need a hand? Tell us.
              </p>

              <div className="flex gap-1.5 mb-3 p-1 bg-s2 rounded-lg">
                {TYPE_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setType(opt.value)}
                    className={`flex-1 py-1.5 rounded-md text-[13px] font-medium cursor-pointer border-0 font-sans transition-colors ${
                      type === opt.value
                        ? 'bg-surface text-ink shadow-sm'
                        : 'bg-transparent text-ink-2'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>

              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={PLACEHOLDERS[type]}
                aria-label="Message"
                rows={4}
                maxLength={5000}
                autoFocus
                className="w-full resize-none rounded-lg border border-line-soft bg-surface px-3 py-2 text-[14px] text-ink font-sans outline-none focus:border-primary"
              />

              {!user && (
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email (optional, if you'd like a reply)"
                  aria-label="Your email"
                  className="w-full mt-2 rounded-lg border border-line-soft bg-surface px-3 py-2 text-[14px] text-ink font-sans outline-none focus:border-primary"
                />
              )}

              {mutation.isError && (
                <p className="m-0 mt-2 text-[13px] text-danger">
                  Something went wrong. Please try again.
                </p>
              )}

              <button
                type="submit"
                disabled={!message.trim() || mutation.isPending}
                className="w-full mt-3 py-2.5 rounded-lg border-0 bg-primary text-on-primary text-[14px] font-semibold cursor-pointer font-sans disabled:opacity-50"
              >
                {mutation.isPending ? 'Sending…' : 'Send'}
              </button>
            </form>
          )}
        </div>
      )}
    </>
  )
}
